/**
 * MessagesPage Component
 *
 * Lists all message threads for the user.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from './components';
import { useMessages } from './hooks';

export const MessagesPage: React.FC = () => {
  const { threads, isLoading } = useMessages();

  return (
    <DashboardLayout title="Messages">
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-4 p-4 bg-gray-100 rounded-xl">
              <div className="w-12 h-12 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : threads.length > 0 ? (
        <div className="space-y-2">
          {threads.map((thread) => (
            <MessageThreadPreview key={thread.id} thread={thread} />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </DashboardLayout>
  );
};

// Message Thread Preview
interface MessageThreadPreviewProps {
  thread: {
    id: string;
    otherParticipant: { id: string; name: string; photo?: string };
    listing?: { id: string; title: string; photo: string };
    lastMessage: { content: string; senderId: string; createdAt: Date };
    unreadCount: number;
  };
}

const formatTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const MessageThreadPreview: React.FC<MessageThreadPreviewProps> = ({ thread }) => {
  const hasUnread = thread.unreadCount > 0;

  return (
    <Link
      to={`/dashboard/messages/${thread.id}`}
      className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${
        hasUnread ? 'bg-primary-50 hover:bg-primary-100' : 'bg-white hover:bg-gray-50 border border-gray-200'
      }`}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        {thread.otherParticipant.photo ? (
          <img
            src={thread.otherParticipant.photo}
            alt={thread.otherParticipant.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-medium">
            {thread.otherParticipant.name.charAt(0)}
          </div>
        )}
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {thread.unreadCount}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className={`font-medium truncate ${hasUnread ? 'text-gray-900' : 'text-gray-700'}`}>
            {thread.otherParticipant.name}
          </h3>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {formatTime(thread.lastMessage.createdAt)}
          </span>
        </div>

        {thread.listing && (
          <p className="text-xs text-gray-500 mb-1 truncate">
            Re: {thread.listing.title}
          </p>
        )}

        <p className={`text-sm truncate ${hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
          {thread.lastMessage.content}
        </p>
      </div>

      {/* Listing Photo */}
      {thread.listing && (
        <div className="hidden sm:block flex-shrink-0">
          <img
            src={thread.listing.photo}
            alt={thread.listing.title}
            className="w-16 h-16 rounded-lg object-cover"
          />
        </div>
      )}
    </Link>
  );
};

// Empty State
const EmptyState: React.FC = () => (
  <div className="text-center py-16 px-4">
    <div className="text-gray-300 mb-4 flex justify-center">
      <MessageIcon className="w-16 h-16" />
    </div>
    <h3 className="text-xl font-medium text-gray-900 mb-2">No messages yet</h3>
    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
      When you contact a host or receive a message, it will appear here.
    </p>
  </div>
);

// Message Icon
const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

export default MessagesPage;
